import * as Yup from 'yup';
import { ApiSanctionClearanceSandboxEntityType } from '../../../api';
import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { screeningClearanceRulesTestingApi } from '../../../apis';
import { ScreeningClearanceRuleTestResult } from './ScreeningClearanceRulesSandbox';
import {
  SalvBanner,
  SalvBannerAccent,
  SalvBannerType,
  SalvButton,
  SalvCard,
  SalvCheckbox,
  SalvDropdown,
  SalvNumberField,
  SalvTextArea,
  SalvTextField,
} from '@salvapp/ui-components';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import { useSearchParams } from 'react-router-dom';
import { decodeBase64, encodeBase64 } from './helper';
import { useMe } from '../../../organisation/useMe';
import { parseErrorMessage } from '../../../common/errors/helpers';

const validationSchema = Yup.object().shape({
  rule: Yup.string().required(),
  useExistingHits: Yup.boolean(),
  entityExternalId: Yup.string().when('useExistingHits', {
    is: false,
    then: (schema) => schema.required(),
  }),
  screeningAlertId: Yup.number().when('useExistingHits', {
    is: true,
    then: (schema) => schema.required(),
  }),
  entityType: Yup.object()
    .nullable()
    .when('useExistingHits', {
      is: false,
      then: () =>
        Yup.object().shape({
          key: Yup.string(),
          label: Yup.string().required(),
          value: Yup.string().required(),
        }),
    }),
});

type EntityTypeOption = {
  key: string;
  value: ApiSanctionClearanceSandboxEntityType;
  label: string;
};

type TestInputPanelValues = {
  entityType: EntityTypeOption;
  rule: string;
  entityExternalId?: string;
  screeningAlertId?: number;
  useExistingHits: boolean;
};

const DEFAULT_ENTITY_TYPES: EntityTypeOption[] = [
  {
    key: 'PERSON',
    value: ApiSanctionClearanceSandboxEntityType.PERSON,
    label: 'Person',
  },
  {
    key: 'TRANSACTION',
    value: ApiSanctionClearanceSandboxEntityType.TRANSACTION,
    label: 'Transaction',
  },
];

const RFI_TYPE: EntityTypeOption = {
  key: 'RFI',
  value: ApiSanctionClearanceSandboxEntityType.RFI,
  label: 'RFI',
};

const ENTITY_TYPE = 'entityType';
const ENTITY_EXTERNAL_ID = 'entityExternalId';
const SCREENING_ALERT_ID = 'screeningAlertId';
const RULE = 'rule';
const USE_EXISTING_HITS = 'useExistingHits';
const SCREENING_ALERT_ID_LABEL = 'Screening Alert ID';

interface Props {
  setResults: (result: ScreeningClearanceRuleTestResult) => void;
}

const ClearanceRuleRestInputPanel = ({ setResults }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [entityTypes, setEntityTypes] = useState(DEFAULT_ENTITY_TYPES);
  const { features } = useMe();
  const entityType = searchParams.get(ENTITY_TYPE);
  const entityExternalId = searchParams.get(ENTITY_EXTERNAL_ID) || undefined;
  const screeningAlertId = searchParams.get(SCREENING_ALERT_ID)
    ? Number(searchParams.get(SCREENING_ALERT_ID))
    : undefined;
  const rule = searchParams.get(RULE);
  const useExistingHits = searchParams.get(USE_EXISTING_HITS) === 'true';

  useEffect(() => {
    const rfiScreeningEnabled = isFeatureEnabled(ORGANISATION_FEATURE.RFI_SCREENING, features);
    if (rfiScreeningEnabled) {
      setEntityTypes([...DEFAULT_ENTITY_TYPES, RFI_TYPE]);
    } else {
      setEntityTypes(DEFAULT_ENTITY_TYPES);
    }
  }, [features]);

  const initialValues: TestInputPanelValues = {
    entityType: entityType ? entityTypes.find((it) => it.key == entityType) || entityTypes[0] : entityTypes[0],
    entityExternalId: entityExternalId,
    screeningAlertId: screeningAlertId,
    rule: decodeBase64(rule) ?? '',
    useExistingHits,
  };

  const submit = async (
    values: TestInputPanelValues,
    { setStatus, setSubmitting }: FormikHelpers<TestInputPanelValues>
  ) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set(RULE, encodeBase64(values.rule));
        newParams.set(USE_EXISTING_HITS, String(values.useExistingHits));
        if (values.useExistingHits) {
          newParams.set(SCREENING_ALERT_ID, String(values.screeningAlertId));
          newParams.delete(ENTITY_TYPE);
          newParams.delete(ENTITY_EXTERNAL_ID);
        } else {
          newParams.set(ENTITY_TYPE, values.entityType.key);
          newParams.set(ENTITY_EXTERNAL_ID, values.entityExternalId!);
          newParams.delete(SCREENING_ALERT_ID);
        }
        return newParams;
      },
      { replace: true }
    );

    try {
      const request = {
        apiScreeningClearanceRuleTestCase: {
          rule: values.rule,
          entityExternalId: values.useExistingHits ? undefined : values.entityExternalId,
          entityType: values.useExistingHits ? undefined : values.entityType.value,
          screeningAlertId: values.useExistingHits ? values.screeningAlertId : undefined,
          useExistingHits: values.useExistingHits,
        },
      };
      const results = await screeningClearanceRulesTestingApi.testRule(request);
      setResults({ results: results });
      setStatus(undefined);
    } catch (e: any) {
      setResults({ results: [] });
      setStatus(await parseErrorMessage(e));
    }
    setSubmitting(false);
  };

  const getIdLabel = (entityTypeValue: ApiSanctionClearanceSandboxEntityType) => {
    return entityTypeValue !== ApiSanctionClearanceSandboxEntityType.RFI ? 'External ID' : 'Alert ID';
  };

  return (
    <Formik validationSchema={validationSchema} onSubmit={submit} initialValues={initialValues} enableReinitialize>
      {({
        isSubmitting,
        values,
        status,
        errors,
        touched,
        setFieldValue,
        resetForm,
      }: FormikProps<TestInputPanelValues>) => {
        const entityExternalIdLabel = getIdLabel(values.entityType.value);
        return (
          <Form>
            <SalvCard>
              <SalvCard.Body>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <SalvTextArea
                    label='JMESPath expression'
                    placeholder='JMESPath expression'
                    value={values.rule}
                    autoSizing
                    minRows={3}
                    onChange={(value) => setFieldValue('rule', value)}
                    isInvalid={touched.rule && !!errors.rule}
                  />
                  {values.entityType.value !== ApiSanctionClearanceSandboxEntityType.RFI && (
                    <SalvCheckbox
                      label='Use existing hits (skip re-screening)'
                      selected={values.useExistingHits}
                      onChange={(selected) => setFieldValue('useExistingHits', selected)}
                    />
                  )}
                  {values.useExistingHits ? (
                    <div style={{ maxWidth: '300px' }}>
                      <SalvNumberField
                        label={SCREENING_ALERT_ID_LABEL}
                        placeholder={SCREENING_ALERT_ID_LABEL}
                        value={values.screeningAlertId}
                        onChange={(value) => setFieldValue('screeningAlertId', value)}
                        isInvalid={touched.screeningAlertId && !!errors.screeningAlertId}
                        minValue={1}
                      />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: '1' }}>
                        <SalvDropdown
                          label='Entity Type'
                          options={entityTypes}
                          value={values.entityType}
                          clearable={false}
                          onChange={([value]) => {
                            setFieldValue('entityType', value);
                          }}
                        />
                      </div>
                      <div style={{ flex: '1' }}>
                        <SalvTextField
                          label={entityExternalIdLabel}
                          placeholder={entityExternalIdLabel}
                          value={values.entityExternalId}
                          onChange={(value) => setFieldValue('entityExternalId', value)}
                          isInvalid={touched.entityExternalId && !!errors.entityExternalId}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                  <SalvButton
                    variant='muted'
                    label='Clear'
                    onClick={() => {
                      resetForm();
                      setResults({ results: [] });
                    }}
                  />
                  <SalvButton variant='primary' type='submit' label='Search' disabled={isSubmitting} />
                </div>
              </SalvCard.Body>
            </SalvCard>
            {status && (
              <SalvBanner
                accent={SalvBannerAccent.LIGHT}
                message={status}
                type={SalvBannerType.DANGER}
                className={'m-t-8'}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default ClearanceRuleRestInputPanel;
